import { inject, Injectable } from '@angular/core';
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { QUERY_KEYS } from '@core/constans';
import { environment } from '@environments/environment';
import { ChatService } from '@core/services/chat.service';
import { ApiResponse } from '@core/types';
import { ChatChannel, ChatChannelCreate, ChatMessage, ChatMessageCreate } from '@core/types/chat.type';

@Injectable({ providedIn: 'root' })
export class ChatQuery {
  private readonly service = inject(ChatService);
  private readonly queryClient = inject(QueryClient);

  useChannels() {
    return injectQuery(() => ({
      queryKey: QUERY_KEYS.chat.channels(),
      queryFn: () => lastValueFrom(this.service.getChannels()),
      select: (res: ApiResponse<ChatChannel[]>) => res.data || [],
      staleTime: environment.staleTime,
    }));
  }

  useMessages(channelId: () => string | null) {
    return injectQuery(() => {
      const id = channelId();
      return {
        queryKey: QUERY_KEYS.chat.messages(id || ''),
        queryFn: () => lastValueFrom(this.service.getMessages(id!)),
        select: (res: ApiResponse<ChatMessage[]>) => res.data || [],
        enabled: !!id,
        staleTime: environment.staleTime,
      };
    });
  }

  useCreateChannel() {
    return injectMutation(() => ({
      mutationFn: (data: ChatChannelCreate) => lastValueFrom(this.service.createChannel(data)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.chat.all }),
    }));
  }

  useDeleteChannel() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.service.deleteChannel(id)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.chat.all }),
    }));
  }

  useSendMessage() {
    return injectMutation(() => ({
      mutationFn: (data: ChatMessageCreate) => lastValueFrom(this.service.sendMessage(data)),
      onSuccess: (_res, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.chat.messages(variables.channelId) });
      },
    }));
  }

  useDeleteMessage() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.service.deleteMessage(id)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.chat.all }),
    }));
  }
}

